import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, FadeUp, EASE, SectionHead } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import CompareChooser from "@/components/CompareChooser";
import SpotlightCard from "@/components/SpotlightCard";
import ROICalculator from "@/components/ROICalculator";
import QuoteBuilder from "@/components/QuoteBuilder";

const HERO_IMG =
  "https://images.unsplash.com/photo-1653315917834-04a6d84e132e?crop=entropy&cs=srgb&fm=jpg&q=85";
const CTA_IMG =
  "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?crop=entropy&cs=srgb&fm=jpg&q=85";

const STATS = [
  ["10 mm", "Horizontal GNSS accuracy"],
  ["100 Hz", "IMU update rate"],
  ["<2 h", "Retrofit install"],
  ["30 min", "Field calibration"],
];

export default function Home() {
  const heroRef = useRef(null);
  const [tab, setTab] = useState("roi");
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const openBusinessCase = (id) => {
    setTab(id);
    document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main>
      <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[100svh] flex-col overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Excavator on a construction site at dusk" className="h-full w-full scale-105 object-cover" />
          <div className="absolute inset-0 bg-ink/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />

        <motion.svg
          viewBox="0 0 400 400"
          className="absolute right-[8%] top-[16%] hidden h-[380px] w-[380px] lg:block"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
          aria-hidden="true"
        >
          <circle cx="200" cy="200" r="150" fill="none" stroke="#10B981" strokeOpacity="0.5" strokeWidth="1" strokeDasharray="4 10" className="origin-center animate-[spin_50s_linear_infinite]" />
          <circle cx="200" cy="200" r="110" fill="none" stroke="#10B981" strokeOpacity="0.35" strokeWidth="1" />
          <line x1="200" y1="20" x2="200" y2="380" stroke="#00FF66" strokeOpacity="0.4" strokeWidth="1" />
          <line x1="20" y1="200" x2="380" y2="200" stroke="#00FF66" strokeOpacity="0.4" strokeWidth="1" />
          <circle cx="200" cy="200" r="5" fill="#00FF66" />
        </motion.svg>

        <motion.div
          style={{ opacity: contentFade }}
          className="relative mx-auto flex w-full max-w-[1600px] flex-1 flex-col justify-end px-4 pb-20 pt-32 sm:px-8 lg:px-16"
        >
          <FadeUp y={12} className="mb-6">
            <span className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-neon sm:text-xs">
              <span className="h-2 w-2 bg-neon" />
              <span>
                Authorized{" "}
                <Link to="/precision-solutions" data-testid="hero-eyebrow-chcnav-link" className="underline decoration-neon/40 underline-offset-4 transition-colors hover:text-white">
                  CHCNAV
                </Link>{" "}
                distributor · Machine control
              </span>
            </span>
          </FadeUp>

          <MaskedLines
            as="h1"
            delay={0.15}
            lines={["Every bucket", { text: "on grade.", lineClass: "italic text-neon" }]}
            className="font-display text-[19vw] font-black uppercase leading-[0.88] tracking-tight text-white sm:text-[14vw] lg:text-[10.5rem]"
          />

          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            2.5D and 3D machine control for excavators and dozers — millimetre-accurate{" "}
            <Link to="/precision-solutions" data-testid="hero-sub-chcnav-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
              CHCNAV
            </Link>{" "}
            guidance, retrofitted
            to your machine. Supported locally by <strong className="font-bold text-neon">Green Grade</strong>.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#quote"
              data-testid="hero-quote-cta"
              onClick={(e) => {
                e.preventDefault();
                setTab("quote");
                document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Build a quote
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <Link
              to="/contact"
              data-testid="hero-demo-cta"
              className="inline-flex items-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
            >
              Request a demo
            </Link>
            <a
              href="#lineup"
              data-testid="hero-systems-link"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("lineup")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Explore our systems
            </a>
          </div>

          <div className="mt-16 grid grid-cols-2 gap-6 border-t border-line/70 pt-8 lg:grid-cols-4" data-testid="hero-stats">
            {STATS.map(([v, l], i) => (
              <FadeUp key={l} delay={0.4 + i * 0.1} y={16}>
                <div className="font-display text-4xl font-black text-white sm:text-5xl">{v}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-fog sm:text-[11px]">{l}</div>
              </FadeUp>
            ))}
          </div>
        </motion.div>
      </section>

      <Marquee />

      <section
        id="quote"
        className="border-y border-line bg-deep px-4 py-24 sm:px-8 lg:px-16 lg:py-40"
        data-testid="roi-section"
      >
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="01 · The business case"
            title="Run your own numbers"
            body="Machine control pays for itself faster than you think. Almost zero reworks, no stakeouts and access to a bigger pool of project opportunities. Crunch the savings — or build your own quote in a minute."
          />
          <FadeUp delay={0.1} className="mt-14">
            <div className="flex w-fit border border-line" data-testid="quote-tabs">
              {[["roi", "Savings calculator"], ["quote", "Build a quote"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`${id}-tab-btn`}
                  onClick={() => setTab(id)}
                  className={`px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] transition-colors sm:px-14 sm:text-sm ${
                    tab === id ? "bg-signal text-deep" : "text-fog hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-10">
            {tab === "roi" ? (
              <ROICalculator
                onBuildQuote={() => {
                  setTab("quote");
                  document.querySelector('[data-testid="quote-tabs"]')?.scrollIntoView({ behavior: "smooth" });
                }}
              />
            ) : (
              <QuoteBuilder />
            )}
          </FadeUp>
        </div>
      </section>

      <Manifesto />

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="home-chooser">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="Quick triage"
            title="Compare to see which system is right for you."
            body="Three quick questions — we'll point you at the right machine before you scroll the lineup."
          />
          <FadeUp delay={0.1} className="mt-12">
            <CompareChooser />
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-6 text-sm text-fog">
              Weighing all three side by side?{" "}
              <Link to="/compare" data-testid="home-chooser-compare-link" className="text-signal underline decoration-signal/40 underline-offset-4 transition-colors hover:text-neon">
                See the full comparison
              </Link>
              .
            </p>
          </FadeUp>
        </div>
      </section>

      <section id="lineup" className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="products-section">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHead kicker="02 · The lineup" title="The full lineup." className="lg:pb-2" />
            <FadeUp delay={0.1}>
              <p className="max-w-md text-base leading-relaxed text-fog lg:pb-2">
                Machine control for the digger, the dozer and the surveyor — every system retrofits to your existing
                fleet.
              </p>
            </FadeUp>
          </div>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            <FadeUp>
              <SpotlightCard
                testid="product-card-25d"
                to="/products/2.5d"
                specTo="/specifications#emg100"
                onPricing={() => openBusinessCase("quote")}
                kicker="2.5D System"
                title="EasyNAV EMG100"
                chips={["±30 mm accuracy", "Dual GNSS", "<2 h install"]}
                img="https://images.unsplash.com/photo-1622082679766-c5912d9416eb?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Excavator cutting grade on brown soil"
              />
            </FadeUp>
            <FadeUp delay={0.12}>
              <SpotlightCard
                testid="product-card-3d"
                to="/products/3d"
                specTo="/specifications#tx73"
                onPricing={() => openBusinessCase("quote")}
                kicker="3D System"
                title="MCNAV TX73"
                chips={["10 mm H / 15 mm V", '10.1" CAD display', "iSite cloud"]}
                img="https://images.pexels.com/photos/10421754/pexels-photo-10421754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Aerial view of an excavator working on site"
              />
            </FadeUp>
            <FadeUp>
              <SpotlightCard
                testid="product-card-dozer"
                to="/products/dozer"
                specTo="/specifications#td63"
                onPricing={() => openBusinessCase("quote")}
                kicker="Dozer System"
                title="TD63 Pro"
                chips={["±20 mm blade accuracy", "Automatic blade control", "30 min calibration"]}
                img="https://images.pexels.com/photos/36523643/pexels-photo-36523643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Top-down view of a dozer moving earth"
              />
            </FadeUp>
            <FadeUp delay={0.12}>
              <SpotlightCard
                testid="product-card-rover"
                to="/products/rover"
                specTo="/specifications#i83"
                onPricing={() => openBusinessCase("quote")}
                kicker="Survey Rover"
                title="i83"
                chips={["8 mm + 1 ppm H", "1408 channels", "Calibration-free tilt"]}
                img="/i83-receiver.jpg"
                alt="CHCNAV i83 GNSS receiver on a survey pole"
              />
            </FadeUp>
            <FadeUp delay={0.24} className="sm:col-span-2">
              <SpotlightCard
                testid="product-card-2d"
                to="/hire"
                specTo="/specifications#tx73"
                onPricing={() => openBusinessCase("quote")}
                kicker="2D Tier · Same TX73 hardware"
                title="TX73 · Start in 2D"
                chips={["Depth & slope", "Laser / benchmark", "Upgrade to 3D any time"]}
                img="https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Excavator cutting depth on site at dusk"
              />
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-line" data-testid="home-cta-banner">
        <img src={CTA_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="relative mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
          <FadeUp>
            <h2 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-white sm:text-7xl lg:text-8xl">
              See it <span className="italic text-neon">dig.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-6 max-w-md text-lg text-slate-300">
              Book an on-site demo on your own machine. No slide decks — just your soil, your bucket, on grade.
            </p>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-banner-btn"
                className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-white"
              >
                Request a demo
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <button
                type="button"
                data-testid="cta-banner-pricing-btn"
                onClick={() => openBusinessCase("quote")}
                className="inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
              >
                See pricing
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
              <button
                type="button"
                data-testid="cta-banner-calculator-btn"
                onClick={() => openBusinessCase("roi")}
                className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
              >
                Savings calculator
              </button>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
